import { useEffect, useId, useState } from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { ChevronDownIcon, MessageCircleIcon } from 'lucide-react';
import { toast } from 'sonner';
import { Drawer } from '../shared/Drawer';
import { useCatalog } from '../../contexts/CatalogContext';
import { useAccount, useAskSarah, useAskSarahActions } from '../../contexts/AccountContext';
import { founder, salon } from '../../data/content';
import { policyCopy } from '../../data/policies';
import { shopLabels } from '../../data/shopLabels';
import { effectivePlan } from '../../utils/account';
import { askContext, askMessage, pickTopic, topicsFor } from '../../utils/askSarah';
import { btnPrimaryLg, inputCls } from '../../ui/styles';
import { ease } from '../../core/format/motion';
import { plural } from '../../core/format/text';

/**
 * Vraag Sarah: een WhatsApp-bericht met het dossier erin — huidplan, laatste
 * bestelling of afspraak. Zij hoeft niets op te zoeken, de klant hoeft niets uit te leggen.
 */
export function AskSarahDrawer() {
  const {
    account
  } = useAccount();
  const {
    askOpen,
    askTopic
  } = useAskSarah();
  const {
    closeAsk
  } = useAskSarahActions();
  const {
    productById,
    routineById,
    treatmentBySlug
  } = useCatalog();
  const reduce = useReducedMotion();
  const contextId = useId();
  const [topic, setTopic] = useState(askTopic);
  const [question, setQuestion] = useState('');
  const [showContext, setShowContext] = useState(false);
  useEffect(() => {
    if (askOpen) setTopic(askTopic);
  }, [askOpen, askTopic]);
  if (!account) return null;
  const {
    items,
    provisional
  } = effectivePlan(account, productById);
  const topics = topicsFor(account, items);
  const t = pickTopic(topics, topic);
  const context = askContext(account, items, provisional, t.key, {
    productName: id => productById(id)?.name ?? id,
    routineName: account.plan.routineId ? routineById(account.plan.routineId)?.name : undefined,
    treatmentName: slug => treatmentBySlug(slug)?.name ?? slug
  });
  const message = askMessage(account.customer.firstName, founder.firstName, t.opener, context, question);
  const href = `${salon.whatsapp}?text=${encodeURIComponent(message)}`;
  return <Drawer open={askOpen} onClose={closeAsk} title={`Vraag ${founder.firstName}`} data-id="element-5833" data-mp="2a83m4ko0hx6w">
      <div className="flex items-center gap-4" data-id="element-5834" data-mp="2f6ol6grj2atr">
        <img src={founder.portrait} alt="" className="h-14 w-14 rounded-full object-cover" data-id="element-5835" data-mp="2f5dhsi1zv536" />
        <div data-id="element-5836" data-mp="208mjkc423zf5">
          <p className="text-[15px] text-ink" data-id="element-5837" data-mp="207up54m747qg">{founder.firstName} kijkt mee in je dossier</p>
          <p className="text-[13px] text-muted" data-id="element-5838" data-mp="25a25e6e269yl">{policyCopy.response}</p>
        </div>
      </div>

      <fieldset className="mt-7" data-id="element-5839" data-mp="1raajljhavpa1">
        <legend className="text-[14px] text-ink" data-id="element-5840" data-mp="1rb2gcxmmvt1w">Waar gaat het over?</legend>
        <div className="mt-2.5 flex flex-wrap gap-1.5" data-id="element-5841" data-mp="1w82l957qyprf">
          {topics.map(x => <button key={x.key} type="button" aria-pressed={t.key === x.key} onClick={() => setTopic(x.key)} className={`whitespace-nowrap rounded-full px-3.5 py-2.5 text-[13px] transition-[background-color,color,box-shadow] duration-150 ease-soft ${t.key === x.key ? 'bg-ink text-shell' : 'text-ink ring-1 ring-line hover:ring-ink/40'}`} data-id="element-5842" data-mp="3lna49r0jf9o6">
              {x.label}
            </button>)}
        </div>
      </fieldset>

      <label htmlFor="ask-question" className="mt-6 block text-[14px] text-ink" data-id="element-5843" data-mp="navtq4eeoyus">
        Je vraag
      </label>
      <textarea id="ask-question" maxLength={2000} value={question} onChange={e => setQuestion(e.target.value)} rows={4} aria-describedby="ask-question-hint" placeholder="Bijv. sinds de retinal prikt mijn huid bij mijn neus. Moet ik minderen?" className={`${inputCls} mt-1.5 resize-none`} data-id="element-5844" data-mp="icaotro7lr00" />
      <p id="ask-question-hint" className="mt-1.5 text-[13px] text-muted" data-id="element-5845" data-mp="2y4zz5y7poi3p">
        Een foto stuur je daarna gewoon mee in WhatsApp.
      </p>

      {context.length > 0 && <div className="mt-6 bg-sand" data-id="element-5846" data-mp="2vhz8ttgiwwr3">
          <button type="button" aria-expanded={showContext} aria-controls={contextId} onClick={() => setShowContext(v => !v)} className="flex w-full items-center justify-between gap-3 px-4 py-3 text-left text-[14px] text-ink" data-id="element-5847" data-mp="30hk701nvd12r">
            <span data-id="element-5848" data-mp="2crzqrqj3ohx6">Dit sturen we mee ({plural(context.length, 'regel', 'regels')})</span>
            <ChevronDownIcon className={`h-4 w-4 text-muted transition-transform duration-200 ease-soft motion-reduce:transition-none ${showContext ? 'rotate-180' : ''}`} strokeWidth={1.5} aria-hidden="true" data-id="element-5849" data-mp="2cq6h02d6xhky" />
          </button>
          <AnimatePresence initial={false} data-id="element-5850" data-mp="2qkpebzea1uwe">
            {showContext && <motion.div id={contextId} initial={reduce ? {
          opacity: 0
        } : {
          height: 0,
          opacity: 0
        }} animate={reduce ? {
          opacity: 1
        } : {
          height: 'auto',
          opacity: 1
        }} exit={reduce ? {
          opacity: 0
        } : {
          height: 0,
          opacity: 0
        }} transition={{
          duration: 0.2,
          ease
        }} className="overflow-hidden" data-id="element-5851" data-mp="1t38szt908ld3">
                <dl className="space-y-2 border-t border-line px-4 py-3" data-id="element-5852" data-mp="1gjtno7n74e05">
                  {context.map(l => <div key={l.label} className="grid grid-cols-[7.5rem_minmax(0,1fr)] gap-2 text-[13px]" data-id="element-5853" data-mp="1clvxb1hovlv6">
                      <dt className="text-muted" data-id="element-5854" data-mp="17mjbmk2k0ku6">{l.label}</dt>
                      <dd className="break-words text-ink" data-id="element-5855" data-mp="12t838ei3fw05">{l.value}</dd>
                    </div>)}
                </dl>
              </motion.div>}
          </AnimatePresence>
        </div>}

      <a href={href} target="_blank" rel="noreferrer" onClick={() => {
      closeAsk();
      // Het bericht gaat pas weg als zij in WhatsApp op versturen tikt; dat zeggen we hier.
      toast('Je bericht staat klaar in WhatsApp', {
        description: `Tik daar op versturen. ${policyCopy.response}`
      });
    }} className={`${btnPrimaryLg} mt-6 w-full`} data-id="element-5856" data-mp="stqxjx2udi46">
        <MessageCircleIcon className="h-4 w-4" strokeWidth={1.5} aria-hidden="true" data-id="element-5857" data-mp="e6s98aza73to" />
        Open in WhatsApp
        <span className="sr-only" data-id="element-5858" data-mp="58f90n8pbmbh">{shopLabels.viaWhatsApp}</span>
      </a>
      <p className="mt-3 text-center text-[12px] text-muted" data-id="element-5859" data-mp="5aj7uqrqtv4w">Je ziet het bericht eerst in WhatsApp en verstuurt het zelf.</p>
    </Drawer>;
}